/**
 * Done-state for the new-club setup checklist on ClubDashboard.
 *
 * The checklist (STEPS in ClubDashboard) teaches the setup order; ra-gbrd.3
 * ticked only the Seasons step. This extends the done-detection to the other
 * steps a fresh club works through, so the checklist reflects real progress
 * instead of always showing the step number (ra-gbrd.12).
 *
 * Detection is deliberately CHEAP — counts and presence checks over data the
 * dashboard already needs to load, never deep validation. The dashboard is
 * club-scoped (not tied to one season), so each season-scoped signal is true
 * when ANY of the club's seasons satisfies it.
 */

/** A course card counts as "set up" once it carries at least one course in its
 *  sequence — a bare card with no courses is a stub, not progress. */
export function cardsHaveCourse(cards) {
  return (
    Array.isArray(cards) &&
    cards.some((c) => Array.isArray(c?.courses) && c.courses.length > 0)
  );
}

/** A season has start times entered once any racetime variant carries a
 *  non-empty `starts` array. Variants persist after publishing, so this also
 *  covers a season whose schedule has already been published. */
export function variantsHaveStarts(variants) {
  return (
    Array.isArray(variants) &&
    variants.some((v) => Array.isArray(v?.starts) && v.starts.length > 0)
  );
}

/**
 * Cheap done-state for the setup checklist, keyed by a step's `to` slug so the
 * dashboard can swap the step number for a ✓. Only steps with a real signal are
 * returned; the caller renders the step number for everything else (Documents,
 * Course Viewer, Race Day, Reports).
 *
 * Signals:
 *   seasons      — ≥1 season (event) exists
 *   fleets       — ≥1 season defines classes (season.classes; ra-gbrd.11 made
 *                  this the canonical class source)
 *   marks        — ≥1 club mark
 *   course-cards — ≥1 draft course card with ≥1 course, OR a bundle has already
 *                  been published (content_version > 0 ⇒ cards existed). Publish
 *                  has no dedicated step card, so the published signal folds in
 *                  here.
 *   racetimes    — ≥1 season has racetime variants with start times entered
 *
 * @param {object} signals
 * @param {Array}  [signals.seasons]              event docs (carry classes, content_version)
 * @param {Array}  [signals.marks]                club marks
 * @param {Array<Array>} [signals.draftCardsPerSeason]  wip course_cards, one array per season
 * @param {Array<Array>} [signals.variantsPerSeason]    racetime variants, one array per season
 * @returns {Object<string, boolean>} done-state keyed by step `to` slug
 */
export function deriveSetupDone({
  seasons = [],
  marks = [],
  draftCardsPerSeason = [],
  variantsPerSeason = [],
} = {}) {
  const list = Array.isArray(seasons) ? seasons : [];
  const hasClasses = list.some(
    (s) => Array.isArray(s?.classes) && s.classes.length > 0,
  );
  const published = list.some((s) => (s?.content_version || 0) > 0);
  return {
    seasons: list.length > 0,
    fleets: hasClasses,
    marks: Array.isArray(marks) && marks.length > 0,
    'course-cards':
      published ||
      (Array.isArray(draftCardsPerSeason) && draftCardsPerSeason.some(cardsHaveCourse)),
    racetimes:
      Array.isArray(variantsPerSeason) && variantsPerSeason.some(variantsHaveStarts),
  };
}
